import React, { useState, useEffect, useCallback, useRef } from "react";
import { ActivityEntry, ActivityResponse, ActivityFilterLevel } from "./types";

interface ScraperJobActivityLogProps {
  jobId: string;
  jobStatus: string;
  scraperApiUrl: string;
  getHeaders: () => Record<string, string>;
}

const FILTER_LEVELS: ActivityFilterLevel[] = ["all", "error", "warning", "success", "info"];

const DOT_COLORS: Record<string, string> = {
  success: "bg-green-500",
  error: "bg-red-500",
  warning: "bg-amber-500",
  info: "bg-blue-400"
};

const TAB_COLORS: Record<string, string> = {
  all: "bg-slate-100 text-slate-700",
  error: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-700",
  success: "bg-green-100 text-green-700",
  info: "bg-blue-100 text-blue-700"
};

const TAB_ACTIVE_COLORS: Record<string, string> = {
  all: "bg-slate-200 text-slate-900 ring-1 ring-slate-300",
  error: "bg-red-200 text-red-900 ring-1 ring-red-300",
  warning: "bg-amber-200 text-amber-900 ring-1 ring-amber-300",
  success: "bg-green-200 text-green-900 ring-1 ring-green-300",
  info: "bg-blue-200 text-blue-900 ring-1 ring-blue-300"
};

export default function ScraperJobActivityLog({
  jobId,
  jobStatus,
  scraperApiUrl,
  getHeaders
}: ScraperJobActivityLogProps) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [counts, setCounts] = useState<ActivityResponse["counts"]>({
    success: 0,
    error: 0,
    warning: 0,
    info: 0
  });
  const [filter, setFilter] = useState<ActivityFilterLevel>("all");
  const [loading, setLoading] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await fetch(
        `${scraperApiUrl}/jobs/${jobId}/activity`,
        { headers: getHeaders() }
      );
      if (response.ok) {
        const data: ActivityResponse = await response.json();
        setEntries(data.entries);
        setCounts(data.counts);
      }
    } catch {
      // Silently fail — will retry on next poll
    } finally {
      setLoading(false);
    }
  }, [jobId, scraperApiUrl, getHeaders]);

  // Fetch on mount + poll while running
  useEffect(() => {
    fetchActivity();

    if (jobStatus === "running" || jobStatus === "pending") {
      const interval = setInterval(fetchActivity, 3000);
      return () => clearInterval(interval);
    }
  }, [jobStatus, fetchActivity]);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [entries, autoScroll]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    setAutoScroll(atBottom);
  };

  const filteredEntries = entries.filter(
    (e) => filter === "all" || e.level === filter
  );

  const formatTime = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleTimeString();
    } catch {
      return "";
    }
  };

  return (
    <tr>
      <td colSpan={6} className="p-0">
        <div className="border-t border-slate-200 bg-slate-50">
          {/* Filter Tabs + Entry Count */}
          <div className="flex items-center justify-between px-3 pt-2 pb-1 border-b border-slate-200 bg-white">
            <div className="flex gap-1">
              {FILTER_LEVELS.map((level) => {
                const count =
                  level === "all"
                    ? entries.length
                    : counts[level];
                return (
                  <button
                    key={level}
                    onClick={() => setFilter(level)}
                    className={`px-2.5 py-1 rounded text-xs font-medium capitalize ${
                      filter === level
                        ? TAB_ACTIVE_COLORS[level]
                        : TAB_COLORS[level]
                    }`}
                  >
                    {level} ({count})
                  </button>
                );
              })}
            </div>
            <span className="text-xs text-slate-400">
              {filteredEntries.length} of {entries.length} entries
            </span>
          </div>

          {/* Log Entries */}
          <div
            ref={logRef}
            onScroll={handleScroll}
            className="max-h-80 overflow-y-auto p-3 font-mono text-xs space-y-0.5"
          >
            {loading ? (
              <div className="text-center text-slate-400 py-4">Loading...</div>
            ) : filteredEntries.length === 0 ? (
              <div className="text-center text-slate-400 py-4">
                {entries.length === 0
                  ? "No activity yet"
                  : "No entries match this filter"}
              </div>
            ) : (
              filteredEntries.map((entry, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 py-0.5 hover:bg-white/50 rounded px-1"
                >
                  <span
                    className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                      DOT_COLORS[entry.level] || DOT_COLORS.info
                    }`}
                  />
                  <span className="text-slate-400 flex-shrink-0">
                    {formatTime(entry.timestamp)}
                  </span>
                  <span className="text-slate-700 break-all">
                    {entry.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
